import React, { useEffect, useState } from "react";
import {
  Autocomplete, Box, Button, Chip, Dialog, DialogTitle, Divider, IconButton,
  InputAdornment, MenuItem, TextField, Tooltip, Typography,
} from "@mui/material";
import { Add, Close, Delete, Edit, Search, BusinessRounded, AccountTreeOutlined } from "@mui/icons-material";
import CustomSnackbar from "../components/Common/CustomSnackbar";
import { useSnackbar } from "../components/Common/hooks/useSnackbar";
import useAuth from "../hoc/AuthProvider/useAuth";
import { Permissions } from "../config/permissions";
import { getDepartmentList, createDepartment, updateDepartment, deleteDepartment } from "../services/departmentService";
import { getCompanyList } from "../services/companyService";

interface CompanyItem { id: number; name: string; }
interface DepartmentDto {
  id: number;
  parentId: number | null;
  parentName: string | null;
  name: string;
  description: string;
  isActive: boolean;
  companies: CompanyItem[];
}

const emptyForm = {
  name: "",
  description: "",
  companyIds: [] as number[],
  parentId: null as number | null,
};

const Departments: React.FC = () => {
  const { havePermission } = useAuth();
  const { showSuccess, showError, snackbarProps } = useSnackbar();

  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterCompanyId, setFilterCompanyId] = useState<number | "">("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<"name" | "companyIds", string>>>({});

  const fetchData = async (companyId?: number) => {
    setLoading(true);
    try {
      const [deptRes, compRes] = await Promise.all([
        getDepartmentList(companyId),
        getCompanyList(),
      ]);
      setDepartments((deptRes.data as any).data ?? []);
      setCompanies((compRes.data as any).data ?? []);
    } catch (e) {
      showError(e as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleFilterChange = (companyId: number | "") => {
    setFilterCompanyId(companyId);
    fetchData(companyId !== "" ? companyId : undefined);
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (d: DepartmentDto) => {
    setEditId(d.id);
    setForm({
      name: d.name,
      description: d.description ?? "",
      companyIds: d.companies.map((c) => c.id),
      parentId: d.parentId,
    });
    setErrors({});
    setDialogOpen(true);
  };

  const handleClose = () => { setDialogOpen(false); setErrors({}); };

  const handleSave = async () => {
    const errs: typeof errors = {};
    if (!form.name.trim()) errs.name = "Departman adı boş olamaz.";
    else if (form.name.trim().length > 200) errs.name = "Departman adı en fazla 200 karakter olabilir.";
    if (form.companyIds.length === 0) errs.companyIds = "En az bir şirket seçiniz.";
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    try {
      if (editId !== null) {
        await updateDepartment({ id: editId, name: form.name, description: form.description, parentId: form.parentId, companyIds: form.companyIds });
        showSuccess("Departman güncellendi.");
      } else {
        await createDepartment({ name: form.name, description: form.description, parentId: form.parentId, companyIds: form.companyIds });
        showSuccess("Departman oluşturuldu.");
      }
      setDialogOpen(false);
      await fetchData(filterCompanyId !== "" ? filterCompanyId : undefined);
    } catch (e) {
      showError(e as Error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await deleteDepartment(id);
      if (!(res.data as any).success) { showError(new Error((res.data as any).message)); return; }
      showSuccess("Departman silindi.");
      await fetchData(filterCompanyId !== "" ? filterCompanyId : undefined);
    } catch (e) {
      showError(e as Error);
    }
  };

  const parentOptions = departments.filter((d) => d.id !== editId);
  const selectedCompanies = companies.filter((c) => form.companyIds.includes(c.id));
  const filtered = departments.filter((d) =>
    d.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>Departmanlar</Typography>
        {havePermission(Permissions.Department_Create) && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}
            sx={{ textTransform: "none", borderRadius: "6px", bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "var(--color-primary-dark)" } }}>
            Yeni Departman
          </Button>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField size="small" placeholder="Ara..." value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ width: 260 }} />
        <TextField select size="small" label="Şirkete Göre Filtrele" value={filterCompanyId}
          onChange={(e) => handleFilterChange(e.target.value === "" ? "" : Number(e.target.value))}
          sx={{ width: 220 }}>
          <MenuItem value="">Tümü</MenuItem>
          {companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </TextField>
      </Box>

      <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #e0e0e0", overflow: "hidden" }}>
        {loading ? (
          <Typography sx={{ p: 3, color: "text.secondary" }}>Yükleniyor...</Typography>
        ) : filtered.length === 0 ? (
          <Typography sx={{ p: 3, color: "text.secondary" }}>Kayıt bulunamadı.</Typography>
        ) : filtered.map((d, i) => (
          <Box key={d.id}>
            {i > 0 && <Divider />}
            <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography fontWeight={600} fontSize={14}>{d.name}</Typography>
                  {d.parentName && (
                    <Tooltip title="Üst Departman" arrow placement="top">
                      <Chip label={d.parentName} size="small"
                        icon={<AccountTreeOutlined sx={{ fontSize: "13px !important" }} />}
                        sx={{ fontSize: 11, bgcolor: "#f8f9fa", color: "#495057", border: "1px solid #dee2e6", "& .MuiChip-icon": { color: "#495057" } }} />
                    </Tooltip>
                  )}
                  {d.companies.map((c) => (
                    <Tooltip key={c.id} title="Şirket" arrow placement="top">
                      <Chip label={c.name} size="small"
                        icon={<BusinessRounded sx={{ fontSize: "13px !important" }} />}
                        sx={{ fontSize: 11, bgcolor: "#eff8ff", color: "#1570ef", border: "1px solid #b2ddff", "& .MuiChip-icon": { color: "#1570ef" } }} />
                    </Tooltip>
                  ))}
                </Box>
                {d.description && (
                  <Typography fontSize={12} color="text.secondary">{d.description}</Typography>
                )}
              </Box>
              {havePermission(Permissions.Department_Update) && (
                <IconButton size="small" onClick={() => openEdit(d)}><Edit fontSize="small" /></IconButton>
              )}
              {havePermission(Permissions.Department_Delete) && (
                <IconButton size="small" color="error" onClick={() => handleDelete(d.id)}><Delete fontSize="small" /></IconButton>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {editId ? "Departmanı Düzenle" : "Yeni Departman"}
          <IconButton onClick={handleClose}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField size="small" label="Departman Adı *" value={form.name}
            onChange={(e) => { setForm({ ...form, name: e.target.value }); setErrors({}); }}
            error={!!errors.name} helperText={errors.name} fullWidth />

          <Autocomplete
            multiple
            size="small"
            options={companies}
            getOptionLabel={(o) => o.name}
            value={selectedCompanies}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, v) => { setForm({ ...form, companyIds: v.map((c) => c.id) }); setErrors((p) => ({ ...p, companyIds: undefined })); }}
            renderInput={(params) => <TextField {...params} label="Şirketler *" placeholder="Şirket seçiniz" error={!!errors.companyIds} helperText={errors.companyIds} />}
          />

          <Autocomplete
            size="small"
            options={parentOptions}
            getOptionLabel={(o) => o.name}
            value={parentOptions.find((o) => o.id === form.parentId) ?? null}
            onChange={(_, v) => setForm({ ...form, parentId: v?.id ?? null })}
            renderInput={(params) => <TextField {...params} label="Üst Departman (opsiyonel)" />}
          />

          <TextField size="small" label="Açıklama" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth multiline rows={2} />
        </Box>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: "none" }}>İptal</Button>
          <Button variant="contained" onClick={handleSave}
            sx={{ textTransform: "none", borderRadius: "6px", bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "var(--color-primary-dark)" } }}>
            Kaydet
          </Button>
        </Box>
      </Dialog>

      <CustomSnackbar {...snackbarProps} />
    </>
  );
};

export default Departments;
